import { FormControl, MenuItem, Select, SelectChangeEvent, Tooltip } from "@mui/material";
import { useTimezone } from "../../utils/TimezoneContext";
import { toast } from "react-toastify";

const COMMON_TIMEZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Anchorage",
  "Pacific/Honolulu",
  "America/Phoenix",
  "America/Indiana/Indianapolis",
  "America/Detroit",
  "America/Boise",
  "America/Juneau",
  "America/Adak",
  "America/Toronto",
  "America/Vancouver",
  "America/Halifax",
  "America/St_Johns",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Moscow",
  "Asia/Tokyo",
  "Asia/Shanghai",
  "Asia/Kolkata",
  "Asia/Dubai",
  "Australia/Sydney",
  "Pacific/Auckland",
  "UTC",
];

function getTimezoneLabel(tz: string): string {
  try {
    const now = new Date();
    const abbr = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "short",
    })
      .formatToParts(now)
      .find((p) => p.type === "timeZoneName")?.value ?? tz;
    return `${tz.replace(/_/g, " ")} (${abbr})`;
  } catch {
    return tz;
  }
}

type TimezoneSelectorProps = {
  disabled?: boolean;
};

export default function TimezoneSelector({ disabled = false }: TimezoneSelectorProps) {
  const { timezone, setTimezone } = useTimezone();

  const handleChange = (event: SelectChangeEvent<string>) => {
    setTimezone(event.target.value);
  };

  const handleDisabledClick = () => {
    toast.info(
      "Timezone cannot be changed for aggregate nutrition data. These dates are calculated in UTC on the server."
    );
  };

  if (disabled) {
    return (
      <Tooltip title="Timezone cannot be changed for aggregate nutrition data">
        <FormControl size="small" sx={{ minWidth: 220 }} onClick={handleDisabledClick}>
          <Select
            value="UTC"
            disabled
            sx={{ fontSize: "0.85rem" }}
            data-testid="timezone-selector">
            <MenuItem value="UTC">UTC</MenuItem>
          </Select>
        </FormControl>
      </Tooltip>
    );
  }

  return (
    <FormControl size="small" sx={{ minWidth: 220 }}>
      <Select
        value={timezone}
        onChange={handleChange}
        sx={{ fontSize: "0.85rem" }}
        data-testid="timezone-selector">
        {COMMON_TIMEZONES.map((tz) => (
          <MenuItem key={tz} value={tz}>
            {getTimezoneLabel(tz)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
